import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { createInjectable } from 'ngxtension/create-injectable';
import { catchError, distinctUntilChanged, EMPTY, filter, map, merge, shareReplay, Subject, switchMap, tap, throwError } from 'rxjs';
import type { UserContextGetResponseType } from '@tig-dpqa-cloud/contract-backend-frontend';
import { HttpClient } from '@angular/common/http';
import { TenantService } from '../tenant/tenant.service';
import { StorageService } from '../storage/storage.service';
import { toSignal } from '@angular/core/rxjs-interop';

export const UserContextService = createInjectable(
  () => {
    const http = inject(HttpClient);
    const router = inject(Router);
    const tenantService = inject(TenantService);
    const storageService = inject(StorageService);

    const refresh$ = new Subject<void>();
    let hasRedirectedToForbiddenPage = false;

    const userContext$ = merge(
      storageService.watch('token').pipe(filter(Boolean), distinctUntilChanged()),
      refresh$,
    ).pipe(
      switchMap(() =>
        http.get<UserContextGetResponseType>('/api/user/context').pipe(
          map((res) => ({
            userPermissions: res.user.permissions,
            tenantConfig: res.tenant,
            userProfile: {
              firstName: res.user.firstName,
              lastName: res.user.lastName,
              email: res.user.email,
              eulaAcceptedAt: res.user.eulaAcceptedAt,
            },
            dataplaneVersion: res.dataplaneVersion,
            possibleTenants: res.possibleTenants,
            isTenantOnboarded: res.isTenantOnboarded,
            credentials: res.credentials,
            couldUpgradeDataplane: res.user.permissions.includes('SETUP_WRITE')
              ? res.couldUpgradeDataplane
              : false,
            latestDataplaneTag: res.user.permissions.includes('SETUP_WRITE')
              ? res.latestDataplaneTag
              : undefined,
            eula: res.eula,
            ifu: res.ifu,
          })),
          tap(({ tenantConfig }) => {
            hasRedirectedToForbiddenPage = false;
            tenantService.currentTenant = tenantConfig?.tenantId;
          }),
          catchError((error: { status?: number }) => {
            if (error.status === 403 && !hasRedirectedToForbiddenPage) {
              hasRedirectedToForbiddenPage = true;
              router.navigate(['/error/403']);
            }
            return throwError(() => 'Failed to fetch user context');
          }),
        ),
      ),
      shareReplay(1),
    );

    const safeUserContext$ = userContext$.pipe(catchError(() => EMPTY));

    return {
      userProfile: toSignal(safeUserContext$.pipe(map((ctx) => ctx.userProfile))),
      userPermissions: toSignal(safeUserContext$.pipe(map((ctx) => ctx.userPermissions))),
      tenantConfig: toSignal(safeUserContext$.pipe(map((ctx) => ctx.tenantConfig))),
      dataplaneVersion: toSignal(safeUserContext$.pipe(map((ctx) => ctx.dataplaneVersion))),
      possibleTenants: toSignal(safeUserContext$.pipe(map((ctx) => ctx.possibleTenants)), { initialValue: [] }),
      isTenantOnboarded: toSignal(safeUserContext$.pipe(map((ctx) => ctx.isTenantOnboarded))),
      credentials: toSignal(safeUserContext$.pipe(map((ctx) => ctx.credentials))),
      couldUpgradeDataplane: toSignal(safeUserContext$.pipe(map((ctx) => ctx.couldUpgradeDataplane))),
      latestDataplaneTag: toSignal(safeUserContext$.pipe(map((ctx) => ctx.latestDataplaneTag))),
      eulaUrl: toSignal(safeUserContext$.pipe(map((ctx) => ctx.eula.url))),
      eulaUploadedAt: toSignal(safeUserContext$.pipe(map((ctx) => ctx.eula.uploadedAt))),
      ifuUrl: toSignal(safeUserContext$.pipe(map((ctx) => ctx.ifu.url))),
      tenantConfigVersion: toSignal(safeUserContext$.pipe(map((ctx) => ctx.tenantConfig?.tenantConfigVersion))),
      cloudRetentionDays: toSignal(safeUserContext$.pipe(map((ctx) => ctx.tenantConfig?.cloudRetentionDays))),
      userContext$,
      refreshUserContext: () => refresh$.next(),
    };
  },
  { providedIn: 'root' },
);
